import comunicacao from '@/assets/competencias/comunicacao.jpg';
import conscienciaSituacional from '@/assets/competencias/consciencia-situacional.jpg';
import lideranca from '@/assets/competencias/lideranca.jpg';
import suporteMutuo from '@/assets/competencias/suporte-mutuo.jpg';

/**
 * As ferramentas que o participante leva.
 *
 * Todas do TeamSTEPPS 3.0 (AHRQ, 2023), que e dominio publico e pode ser
 * traduzido e adaptado com atribuicao. Agrupadas pelas quatro competencias do
 * proprio framework.
 *
 * Quatro competencias com foto, mais duas camadas sem foto logo abaixo (ver
 * `camadas`). Em 19/10 o grupo tinha reduzido a quatro; em 20/10 a revisao da
 * Kelly trouxe as outras duas de volta, como camada e nao como competencia.
 *
 * Ferramenta com nome converte melhor que competencia abstrata. Foi o que os
 * dois documentos do briefing apontaram, e handoff e safety huddle apareceram
 * espontaneamente nas respostas dos criadores.
 */
export interface GrupoDeFerramentas {
  competencia: string;
  texto: string;
  ferramentas: string[];
  /**
   * Foto conceitual da competencia, aviacao e saude na mesma imagem.
   *
   * Sem foto, o card aparece marcado em rascunho e sem buraco nenhum na
   * versao publicada.
   */
  imagem?: string;
  alt?: string;
  /** De quem ou do que a foto que falta depende. So aparece em rascunho. */
  depende?: string;
}

export const ferramentas: GrupoDeFerramentas[] = [
  {
    competencia: 'Comunicação',
    texto: 'Transmitir informação crítica com clareza e confirmar que ela foi compreendida.',
    ferramentas: ['SBAR', 'Call-out', 'Check-back', 'Teach-back', 'Handoff estruturado', 'I-PASS'],
    imagem: comunicacao,
    alt: 'Piloto falando ao headset e profissional de saúde falando com a colega sobre a maca.',
  },
  {
    competencia: 'Liderança de equipe',
    texto:
      'Alinhar a equipe antes, reorganizar durante e criar condições para um desempenho coordenado.',
    ferramentas: ['Briefing', 'Huddle', 'Distribuição de papéis', 'Compartilhamento de informações'],
    imagem: lideranca,
    alt: 'Dois pilotos conferindo uma carta na ponte de embarque e três profissionais de saúde em roda em volta de uma prancheta.',
  },
  {
    competencia: 'Consciência situacional',
    texto:
      'Perceber o que está acontecendo, antecipar riscos e reconhecer quando o cenário está mudando.',
    ferramentas: ['STEP', "I'M SAFE", 'STAR', 'Cross-monitoring'],
    imagem: conscienciaSituacional,
    alt: 'Horizonte artificial de um cockpit e enfermeira lendo o monitor do paciente.',
  },
  {
    competencia: 'Suporte mútuo',
    texto:
      'Criar condições para que alguém possa oferecer ajuda ou levantar uma preocupação diante de um risco, inclusive quando a hierarquia pesa.',
    ferramentas: ['CUS', 'Two-Challenge Rule', 'DESC', 'Comunicação assertiva'],
    imagem: suporteMutuo,
    alt: 'Um piloto apontando um comando no painel do outro. Uma profissional de saúde apontando a seringa que o colega segura.',
  },
];

/**
 * As duas camadas, depois das quatro competencias.
 *
 * Voltaram em 20/10 pela revisao da Kelly, mas como camada e nao como
 * competencia: sem foto e num molde mais simples. As quatro competencias sao
 * o TeamSTEPPS; estas duas mostram o que acontece quando as ferramentas viram
 * sistema e quando o aprendizado chega a unidade.
 *
 * Das competencias sairam so as duas que a Kelly marcou com X no slide 5:
 * debriefing, de Lideranca, e modelo mental compartilhado, de Consciencia
 * situacional. Huddle e cross-monitoring aparecem nos dois lugares, porque e
 * assim no texto dela, e o grupo confirmou em 20/10.
 */
export const camadas: {
  competencia: string;
  subtitulo: string;
  texto: string;
  ferramentas: string[];
}[] = [
  {
    competencia: 'Alta confiabilidade',
    subtitulo: 'Integração das ferramentas ao trabalho da equipe.',
    texto:
      'Antecipar riscos, manter a equipe alinhada e responder de forma coordenada quando o cenário muda.',
    ferramentas: ['Huddle', 'Cross-monitoring', 'Modelo mental compartilhado'],
  },
  {
    competencia: 'Aprendizagem e implementação',
    subtitulo: 'Aplicação do aprendizado no cotidiano.',
    texto:
      'Refletir sobre a experiência, reconhecer barreiras e transformar o aprendizado em uma primeira mudança concreta na realidade da equipe.',
    ferramentas: ['Debriefing', 'Barreiras à implementação', 'Formulário de Implementação HCRM'],
  },
];

/**
 * O fecho do bloco.
 *
 * Responde a objecao que o proprio bloco cria: se as ferramentas tem nome e
 * sigla, por que precisar de um curso presencial para aprender?
 */
export const daFerramentaAoComportamento = {
  titulo: 'Da ferramenta ao comportamento',
  texto:
    'Conhecer uma ferramenta não significa conseguir utilizá-la sob pressão. Na Imersão HCRM, você aprende o que usar, quando usar e como adaptar cada recurso ao contexto da saúde, praticando durante as simulações.',
  remate:
    'Porque, sob pressão, não basta saber que é preciso se comunicar melhor. A equipe precisa saber como.',
} as const;
